import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BiometricAuthService } from '../../core/services/auth/biometric-auth.service';
import { PinAuthService } from '../../core/services/auth/pin-auth.service';
import { PinPadComponent } from '../pin-pad/pin-pad.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgOptimizedImage } from '@angular/common';
import { SvgIconComponent } from '@ui-lib/components';

type PinSetupState = 'ENTER_PIN' | 'CONFIRM_PIN' | 'BIOMETRIC';

@Component({
  selector: 'rrp-pin-setup',
  templateUrl: './pin-setup.component.html',
  styleUrl: './pin-setup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PinPadComponent,
    TranslateModule,
    NgOptimizedImage,
    SvgIconComponent,
  ],
})
export class PinSetupComponent implements OnInit {
  private readonly biometricService = inject(BiometricAuthService);
  private readonly pinAuthService = inject(PinAuthService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  protected readonly pinPad = viewChild<PinPadComponent>('pinPad');

  protected readonly state = signal<PinSetupState>('ENTER_PIN');

  private firstPin = '';

  protected readonly biometricAvailable = signal(false);
  protected readonly biometryTypeName = signal('pin.types.default');

  protected readonly errorKey = signal<string | undefined>(undefined);

  protected readonly isLoading = signal(false);

  public async ngOnInit(): Promise<void> {
    await this.checkBiometric();
  }

  private async checkBiometric(): Promise<void> {
    try {
      const status = await this.biometricService.getStatus();
      this.biometricAvailable.set(status.isStrongAvailable);

      if (status.isStrongAvailable) {
        this.biometryTypeName.set(
          this.biometricService.getBiometryTypeName(status.biometryType),
        );
      }
    } catch {
      this.biometricAvailable.set(false);
    }
  }

  protected getTitleKey(): string {
    switch (this.state()) {
      case 'ENTER_PIN':
        return 'pin.setup.enter-new';
      case 'CONFIRM_PIN':
        return 'pin.setup.confirm';
      default:
        return '';
    }
  }

  protected async onPinEntered(pin: string): Promise<void> {
    const currentState = this.state();

    if (currentState === 'ENTER_PIN') {
      this.firstPin = pin;
      this.state.set('CONFIRM_PIN');
      this.errorKey.set(undefined);

      setTimeout(() => this.pinPad()?.reset(), 100);
      return;
    }

    if (currentState === 'CONFIRM_PIN') {
      if (pin !== this.firstPin) {
        this.errorKey.set('pin.setup.error.mismatch');
        this.pinPad()?.reset();
        return;
      }

      this.isLoading.set(true);

      try {
        await this.pinAuthService.setPin(pin);

        if (this.biometricAvailable()) {
          this.state.set('BIOMETRIC');
        } else {
          await this.completeSetup(false);
        }
      } catch (error) {
        console.error('Failed to set PIN:', error);
        this.errorKey.set('pin.setup.error.save-failed');
        this.pinPad()?.reset();
      } finally {
        this.isLoading.set(false);
      }
    }
  }

  protected async onEnableBiometric(): Promise<void> {
    this.isLoading.set(true);

    try {
      await this.biometricService.authenticate({
        reason: this.translateService.instant('pin.setup.reason'),
        cancelTitle: this.translateService.instant('pin.setup.cancel-title'),
      });

      await this.pinAuthService.setBiometricEnabled(true);
      await this.completeSetup(true);
    } catch (error) {
      if (this.biometricService.isUserCancel(error)) {
        return;
      }

      if (this.biometricService.isBiometryLockout(error)) {
        this.errorKey.set('pin.biometric.error.lockout');
        return;
      }

      console.error('Biometric authentication failed:', error);
      this.errorKey.set('pin.biometric.error.failed');
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async onSkipBiometric(): Promise<void> {
    await this.completeSetup(false);
  }

  protected onBack(): void {
    const currentState = this.state();

    if (currentState === 'CONFIRM_PIN') {
      this.state.set('ENTER_PIN');
      this.firstPin = '';
      this.errorKey.set(undefined);
      setTimeout(() => this.pinPad()?.reset(), 100);
    }
  }

  private async completeSetup(biometricEnabled: boolean): Promise<void> {
    if (!biometricEnabled) {
      await this.pinAuthService.setBiometricEnabled(false);
    }

    await this.router.navigate(['/mining']);
  }
}
